import React, { useState, useEffect, useRef } from "react";
import { TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { TSku } from "models/inventory/interfaces/sku.interface";
import { Button, Typography, useDevice } from "design_system/src";
import { ReactComponent as GPS } from "design_system/src/static/icons/gps.svg";
import { ReactComponent as DisckBrake } from "design_system/src/static/icons/disck-brake.svg";
import { ReactComponent as PetrolStation } from "design_system/src/static/icons/petrol-station.svg";
import { ReactComponent as Settings } from "design_system/src/static/icons/settings.svg";
import { ReactComponent as MotorcycleShock } from "design_system/src/static/icons/motorcycle-shock.svg";
import { ReactComponent as DocumentUi } from "design_system/src/static/icons/document-ui.svg";
import { vehicleDetailData } from "models/inventory/interfaces/vehicle-detail.interfaces";
import { capitalize } from "lodash";
import { translateBrakeSystem, translateFrontSuspension, translateTransmission } from "views/VehicleDetail/helpers/translations";
import styles from "./Description.module.scss";

interface EquipmentItem {
  icon: React.ReactElement;
  title: string;
  text: string;
}

interface DescriptionProps {
  vehicle?: TVehicle;
  sku?: TSku;
  vehicleDetails?: vehicleDetailData;
}

const Description = ({ vehicle, sku, vehicleDetails }: DescriptionProps) => {
  const { isMobile } = useDevice();
  const shortDescriptionRef = useRef<HTMLDivElement>(null);
  const [equipments, setEquipments] = useState<EquipmentItem[]>([
    {
      icon: <DocumentUi className="text_primary_300" />,
      title: "Garantía",
      text: !vehicle || vehicle.isNewVehicle ? "Garantía del fabricante" : "3 meses"
    }
  ]);

  useEffect(() => {
    if (vehicleDetails) {
      setEquipments([
        {
          icon: <DisckBrake className="text_primary_50" />,
          title: "Frenos",
          text: `${capitalize(translateBrakeSystem(vehicleDetails.frontBrakes))} delantero, ${capitalize(translateBrakeSystem(vehicleDetails.rearBrakes))} trasero`
        },
        {
          icon: <PetrolStation className="text_primary_300" />,
          title: "Rendimiento",
          text: `Hasta ${vehicleDetails.fuelEfficiencyKml} km/l`
        },
        {
          icon: <Settings className="text_primary_300" />,
          title: "Transmisión",
          text: `${capitalize(translateTransmission(vehicleDetails.transmissionType))}`
        },
        {
          icon: <MotorcycleShock className="text_primary_300" />,
          title: "Suspensión",
          text: `${capitalize(translateFrontSuspension(vehicleDetails.frontSuspension))}`
        },
        {
          icon: <DocumentUi className="text_primary_300" />,
          title: "Garantía",
          text: "Garantía del fabricante"
        },
      ].filter((item) => !item.text.includes("undefined")));
    }
  }, [vehicleDetails]);

  useEffect(() => {
    const text = vehicle?.shortDescription ?? sku?.description;
    if (text && shortDescriptionRef.current) {
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|ftp:\/\/[^\s]+)/g;

      const html = text.replace(urlRegex, url => {
        let href = url;
        if (href.startsWith("www.")) {
          href = `https://${href}`;
        }
        return `<a href="${href}" target="_blank">${url}</a>`;
      });

      shortDescriptionRef.current.innerHTML = `<p>${html}</p>`;
    }

  }, [vehicle, sku, shortDescriptionRef]);

  return (
    <div>
      {sku?.generalDescription && (
        <Typography
          scale="medium"
          weight="400"
          className="m_b_md"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {sku.generalDescription}
        </Typography>
      )}

      {!sku?.generalDescription && (
        <Typography
          scale="medium"
          weight="400"
          className="m_b_md text_neutral_500"
          style={{ whiteSpace: "pre-wrap" }}
        >
          Sin descripción
        </Typography>
      )}

      <div className="m_b_md">
        <Typography
          scale="large"
          weight="600"
          className="m_b_sm"
          component="h2"
        >
          Equipamento destacado:
        </Typography>
        <div className="display_flex flex_wrap flex_align_strench flex_justify_center_mobile">
          {equipments.map((item, index) => {
            return (
              <div
                key={`equipment-${index}`}
                className={`p_b_sm ${styles.equipmentContainer}${equipments.length === index + 1 && !isMobile ? ` ${styles["equipmentContainer--no-padding"]}` : ""}`}
                style={{
                  width: isMobile ? "50%" : `${100 / equipments.length}%`,
                }}
              >
                <div
                  className={styles.equipment}
                  style={{
                    height: "100%",
                    minHeight: "100px",
                    maxHeight: isMobile ? "125px" : `${equipments.length < 5 ? "120px" : "135px"}`
                  }}
                >
                  <div className="h_30_px w_30_px">
                    {item.icon}
                  </div>
                  <Typography
                    scale="small"
                    weight="600"
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    scale="xsmall"
                    weight="400"
                    className="text_center"
                  >
                    {item.text}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="m_b_md">
        <Typography
          scale="large"
          weight="600"
          className="m_b_sm"
        >
          ¿Dónde recogerás tu moto?
        </Typography>
        <div>
          <div className="display_flex flex_gap_xxs">
            <GPS className="w_lg h_lg text_primary_300" />
            <div>

              <Typography
                weight="600"
                scale="small"
              >
                Sede Anáhuac
              </Typography>

              <a href="https://maps.app.goo.gl/hWWWDqwB8d58h3ih7" target="_blank" rel="noreferrer">
                <Typography
                  weight="400"
                  scale="small"
                >
                  Laguna de Mayran 396, Anáhuac I Secc., Anáhuac I Secc, Miguel Hidalgo, 11320 Ciudad de México, CDMX, México.
                </Typography>
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Description;
